import { Stack } from "@mui/material";
import { AssetTypeSelector, StrikeSelector } from "../../../controls";
import { ExpirationSelector } from "../../../../shared/controls";
import { dateTimeConversionService } from "../../../../../shared/services/DateTimeConversionService";
import { useEffect, useState } from "react";
import { AssetType } from "../../../../../shared/types/AssetType";
import { optionParametersSelectorManager } from "../../../utils/OptionParametersSelectorManager";
import { assetService } from "../../../../../shared/services/AssetService";
import { OptionInfoDto } from "../../../../../screens/options-table/types/OptionInfoDto";
import { useAppContext } from "../../../../../shared/contexts/app-context/components/AppContext";
import { ControlGroup } from "../views/ControlGroup";
import {
    BlackScholesModelCalculationService
} from "../../../../../shared/services/BlackScholesModelCalculationService";
import { portfolioMappers } from "../../../../../shared/mappers/PortfolioMappers";
import {
    usePortfolioSelection,
} from "../../../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { OrderDetailsState } from "./OrderDetails";
import { oderManagerUtils } from "../utils/OrderManagerUtils";
import { PricingDetailsState } from "./PricingParameters";

export type ContractDetailsState = {
    assetType: AssetType;
    expiration: string;
    strike: number | null;
    symbol: string;
};

type ContractDetailsProps = {
    contractDetails: ContractDetailsState;
    handleContractDetailsChange: (patch: Partial<ContractDetailsState>) => void;
    orderDetails: OrderDetailsState;
    handleOrderDetailsChange: (patch: Partial<OrderDetailsState>) => void;
    handlePricingDetailsChange: (patch: Partial<PricingDetailsState>) => void;
}

export function ContractDetails({
    contractDetails,
    handleContractDetailsChange,
    orderDetails,
    handleOrderDetailsChange,
    handlePricingDetailsChange,
}: ContractDetailsProps) {
    const {
        marketDataDto,
    } = useAppContext();

    const {
        selectedPortfolio,
    } = usePortfolioSelection();
    
    const [expirations, setExpirations] = useState<string[]>([]);
    const [strikes, setStrikes] = useState<number[]>([]);

    useEffect(() => {
        if (marketDataDto === undefined) {
            return;
        }
        
        if (assetService.isOptionType(contractDetails.assetType)){
            const availableExpirations = marketDataDto.Expirations.map(
                x => dateTimeConversionService.convertDateToLongFormattedString(x));
            
            const availableStrikes = optionParametersSelectorManager.getStrikes(
                marketDataDto.OptionInfos, contractDetails.assetType,
                dateTimeConversionService.parseToUtcDate(contractDetails.expiration),
                (x, y) => x > y ? 1 : -1)
            
            setExpirations(availableExpirations);
            setStrikes(availableStrikes);
        } else {
            setExpirations([]);
            setStrikes([]);
        }
    }, [contractDetails]);
    
    const handleTypeSelection = (newValue: AssetType): void => {
        if (marketDataDto === undefined) {
            return;
        }

        const availableExpirations = assetService.isOptionType(newValue)
            ? marketDataDto.Expirations.map(x => dateTimeConversionService.convertDateToLongFormattedString(x))
            : [];

        setExpirations(availableExpirations);

        if (assetService.isOptionType(newValue)){
            const currentExpiration = contractDetails.expiration !== ''
                ? contractDetails.expiration
                : availableExpirations[0];

            const availableStrikes = optionParametersSelectorManager.getStrikes(
                marketDataDto.OptionInfos, newValue,
                dateTimeConversionService.parseToUtcDate(currentExpiration),
                (x, y) => x > y ? 1 : -1)

            const currentStrike = contractDetails.strike !== null && contractDetails.strike !== 0
                ? contractDetails.strike!
                : availableStrikes[0];

            setStrikes(availableStrikes);
            
            const optionInfoDto = optionParametersSelectorManager.getOption(
                marketDataDto.OptionInfos, newValue,
                dateTimeConversionService.parseToUtcDate(currentExpiration),
                currentStrike);

            const newContractDetails = {
                assetType: newValue,
                expiration: currentExpiration,
                strike: currentStrike,
                symbol: optionInfoDto?.Symbol ?? '',
            };
            
            handleContractDetailsChange(newContractDetails);
            oderManagerUtils.updatePricingParametersWithCurrentMarketValues(marketDataDto!, newContractDetails, handlePricingDetailsChange);
            refreshOptionParameters(optionInfoDto);
        } else {
            setStrikes([]);

            const newContractDetails = {
                assetType: newValue,
                expiration: '',
                strike: null,
                symbol: `${marketDataDto.BaseAsset.BaseAsset}/USDT`,
            };
            
            handleContractDetailsChange(newContractDetails);
            oderManagerUtils.updatePricingParametersWithCurrentMarketValues(marketDataDto!, newContractDetails, handlePricingDetailsChange);
            refreshFeatureParameters();
        }
    }

    const handleExpirationSelectionChange = async (newValue: string): Promise<void> => {
        if (marketDataDto === undefined) {
            return;
        }

        const availableStrikes = optionParametersSelectorManager.getStrikes(
            marketDataDto.OptionInfos, contractDetails.assetType,
            dateTimeConversionService.parseToUtcDate(newValue),
            (x, y) => x > y ? 1 : -1)

        setStrikes(availableStrikes);
        
        let optionInfoDto: OptionInfoDto | null = null;

        if (availableStrikes.some(x => x === contractDetails.strike!)) {
            optionInfoDto = optionParametersSelectorManager.getOption(
                marketDataDto.OptionInfos, contractDetails.assetType,
                dateTimeConversionService.parseToUtcDate(newValue),
                contractDetails.strike!);

            const newContractDetails = {
                assetType: contractDetails.assetType,
                expiration: newValue,
                strike: contractDetails.strike,
                symbol: optionInfoDto?.Symbol ?? '',
            };
            handleContractDetailsChange(newContractDetails);
            oderManagerUtils.updatePricingParametersWithCurrentMarketValues(marketDataDto!, newContractDetails, handlePricingDetailsChange);
        } else {
            const targetStrike = optionParametersSelectorManager.getClosestStrike(availableStrikes, contractDetails.strike!);

            if (targetStrike !== null) {
                optionInfoDto = optionParametersSelectorManager.getOption(
                    marketDataDto.OptionInfos, contractDetails.assetType,
                    dateTimeConversionService.parseToUtcDate(newValue),
                    targetStrike!);

                const newContractDetails = {
                    assetType: contractDetails.assetType,
                    expiration: newValue,
                    strike: targetStrike,
                    symbol: optionInfoDto?.Symbol ?? '',
                };
                handleContractDetailsChange(newContractDetails);
                oderManagerUtils.updatePricingParametersWithCurrentMarketValues(marketDataDto!, newContractDetails, handlePricingDetailsChange);
            }
        }
        
        refreshOptionParameters(optionInfoDto);
    }

    const handleStrikeSelectionChange = (newValue: number): void => {
        if (marketDataDto === undefined) {
            return;
        }

        const optionInfoDto = optionParametersSelectorManager.getOption(
            marketDataDto.OptionInfos, contractDetails.assetType,
            dateTimeConversionService.parseToUtcDate(contractDetails.expiration),
            newValue!);

        const newContractDetails = {
            assetType: contractDetails.assetType,
            expiration: contractDetails.expiration,
            strike: newValue,
            symbol: optionInfoDto?.Symbol ?? '',
        };
        
        handleContractDetailsChange(newContractDetails);
        refreshOptionParameters(optionInfoDto);
        oderManagerUtils.updatePricingParametersWithCurrentMarketValues(marketDataDto!, newContractDetails, handlePricingDetailsChange);
    }

    const refreshOptionParameters = (optionInfoDto: OptionInfoDto | null): void => {
        if (optionInfoDto !== null && marketDataDto !== undefined && marketDataDto !== null &&
            selectedPortfolio !== null && selectedPortfolio.portfolioSettings !== null
        ) {
            const underlyingPrice = selectedPortfolio.portfolioSettings.useCustomPrice
                ? selectedPortfolio.portfolioSettings.customPrice
                : marketDataDto.UnderlyingPrice;

            if (orderDetails.size === null) {
                return;
            }

            const riskFreeInterestValue = optionInfoDto.RiskFreeInterest
                ?? BlackScholesModelCalculationService.riskFreeRate;

            const { price, delta, gamma, vega, theta } =
                portfolioMappers.getAssetParameters(optionInfoDto.Type,
                    optionInfoDto.Strike, optionInfoDto.ExpireAt,
                    optionInfoDto.MarkImpliedVolatility, riskFreeInterestValue, underlyingPrice);

            const size = orderDetails.size;

            handleOrderDetailsChange({
                impliedVolatilityAtExecution: optionInfoDto.MarkImpliedVolatility,
                underlyingPriceAtExecution: marketDataDto.UnderlyingPrice!,
                price: price,
                volume: -price * size,
                delta: delta * size,
                gamma: (gamma ?? 0) * size,
                vega: (vega ?? 0) * size,
                theta: (theta ?? 0) * size,
            });
        } else {
            handleOrderDetailsChange({
                delta: 0,
                gamma: 0,
                vega: 0,
                theta: 0,
            });
        }
    }

    const refreshFeatureParameters = (): void => {
        if (marketDataDto == undefined) {
            return;
        }
        
        const size = orderDetails.size;
        
        handleOrderDetailsChange({
            impliedVolatilityAtExecution: 0,
            underlyingPriceAtExecution: marketDataDto.UnderlyingPrice!,
            price: marketDataDto.UnderlyingPrice ?? 0,
            volume: -orderDetails.price * size,
            delta: Math.abs(size),
            gamma: 0,
            vega: 0,
            theta: 0,
        });
    }
    
    return (
        <ControlGroup title="Contract details">
            <Stack direction="row" spacing={2}>
                <AssetTypeSelector
                    value={contractDetails.assetType}
                    onValueChange={handleTypeSelection}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                />
                <ExpirationSelector
                    expirations={expirations}
                    value={contractDetails.expiration}
                    onValueChange={handleExpirationSelectionChange}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                />
                <StrikeSelector
                    strikes={strikes}
                    value={contractDetails.strike!}
                    onValueChange={handleStrikeSelectionChange}
                    disabled={oderManagerUtils.isOriginalOrderArchived(orderDetails)}
                />
            </Stack>
        </ControlGroup>
    )
}
